import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  doc,
  getDoc,
  collectionGroup,
} from "firebase/firestore";
import { firestoreProd, firestoreDev } from "../../firebase";
import {
  FaSearch,
  FaUser,
  FaBuilding,
  FaFileAlt,
  FaDatabase,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaSpinner,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaList,
  FaFile,
  FaCreditCard,
  FaHome,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { apiUrl, apiUrlDev, apiUrlLocal } from "../../globalVariables";

// ─── Constants ───────────────────────────────────────────────────────────────

const SEARCH_TYPES = [
  { value: "users", label: "Users", icon: FaUser, description: "name, email, phone, ID" },
  { value: "organizations", label: "Organizations", icon: FaBuilding, description: "name, owner, ID" },
  { value: "loans", label: "Loans", icon: FaFileAlt, description: "borrower, loan #, address, LO" },
  { value: "documents", label: "Documents", icon: FaFile, description: "name, type, classification, borrower" },
  { value: "all", label: "All", icon: FaSearch, description: "Search across everything" },
];

const SUBSCRIPTION_FILTERS = [
  { value: "", label: "Any" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "trialing", label: "Trialing" },
];

const STAGE_FILTERS = [
  { value: "", label: "Any stage" },
  { value: "Processing", label: "Processing" },
  { value: "Underwriting", label: "Underwriting" },
  { value: "Closing", label: "Closing" },
  { value: "Funded", label: "Funded" },
  { value: "Denied", label: "Denied" },
];

const MAX_RESULTS_PER_TYPE = 50;
const FETCH_LIMIT = 1000;

// ─── Fuzzy / Scoring Helpers ──────────────────────────────────────────────────

/**
 * Tokenise a string into lowercase words (split on whitespace, dots, dashes, underscores).
 */
const tokenise = (str) =>
  String(str || "")
    .toLowerCase()
    .split(/[\s.\-_@,/]+/)
    .filter(Boolean);

/**
 * Score a document against a query term.
 * Returns 0 if no match, >0 otherwise (higher = better match).
 * Scoring tiers:
 *   3 – exact full-string match on a field
 *   2 – field starts with the query
 *   1 – every query token appears somewhere in the field (partial / fuzzy)
 */
const scoreDoc = (fields, term) => {
  if (!term) return 1; // no term = everything matches (used for filter-only searches)
  const q = term.toLowerCase().trim();
  const qTokens = tokenise(q);
  let best = 0;

  for (const raw of fields) {
    if (raw == null) continue;
    const f = String(raw).toLowerCase();
    if (f === q) { best = Math.max(best, 3); continue; }
    if (f.startsWith(q)) { best = Math.max(best, 2); continue; }
    // All query tokens present in field (handles "john smith" -> "smith john")
    if (qTokens.length > 0 && qTokens.every((t) => f.includes(t))) {
      best = Math.max(best, 1);
    }
  }
  return best;
};

/** Flatten all searchable string values out of a user document. */
const userFields = (u) => [
  u.email, u.name, u.displayName, u.firstName, u.lastName,
  u.phone, u.phoneNumber, u.id, u.organizationId, u.defaultForUserId,
  u.role, u.accountType, u.subscriptionStatus, u.subscriptionPlan,
];

/** Flatten all searchable string values out of an org document. */
const orgFields = (o) => {
  const sub = o.subscription || {};
  return [
    o.name, o.organizationName, o.companyName,
    o.email, o.ownerEmail, o.id,
    o.defaultForUserId, o.ownerId,
    o.industry, o.website,
    o.subscriptionStatus, o.subscriptionPlan,
    sub.status, sub.customerId, sub.priceId,
  ];
};

/** Flatten all searchable string values out of a loan document. */
const loanFields = (l) => {
  const borrowers = l.borrowers || [];
  const assignees = l.assignees || [];
  const bStr = borrowers.flatMap((b) => [
    b.firstName, b.lastName, b.email, b.phone, b.name,
    b.ssn, b.cellPhone, b.workPhone,
  ]);
  const aStr = assignees.flatMap((a) => [a.name, a.email, a.id]);
  return [
    l.id, l.loanNumber, l.losId, l.externalId,
    l.address, l.propertyAddress, l.city, l.state, l.zipCode,
    l.authorName, l.authorEmail, l.loanOfficerName, l.loanOfficerEmail,
    l.name, l.stage, l.purpose, l.loanType,
    l.organizationId,
    ...bStr, ...aStr,
  ];
};

/** Flatten all searchable string values out of a document. */
const docFields = (d) => [
  d.name, d.originalName, d.fileName,
  d.borrowerFirstName, d.borrowerLastName,
  d.uploadedBy, d.uploadedByEmail,
  d.type, d.classification, d.group, d.status,
  d.id, d.loanId, d.organizationId,
  d.textContent,
];

// ─── Search Functions ─────────────────────────────────────────────────────────

const searchUsers = async (firestore, term) => {
  const ref = collection(firestore, "users");
  const q = query(ref, limit(FETCH_LIMIT));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return docs
    .map((d) => ({ ...d, _score: scoreDoc(userFields(d), term) }))
    .filter((d) => d._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, MAX_RESULTS_PER_TYPE);
};

const searchOrganizations = async (firestore, term, subscriptionFilter) => {
  const ref = collection(firestore, "organizations");
  const q = query(ref, limit(FETCH_LIMIT));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return docs
    .filter((d) => {
      if (subscriptionFilter) {
        const status = d.subscription?.status || d.subscriptionStatus;
        if (status !== subscriptionFilter) return false;
      }
      return true;
    })
    .map((d) => ({ ...d, _score: scoreDoc(orgFields(d), term) }))
    .filter((d) => d._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, MAX_RESULTS_PER_TYPE);
};

const searchLoans = async (firestore, term, stageFilter) => {
  const ref = collectionGroup(firestore, "loans");
  const q = query(ref, limit(FETCH_LIMIT));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => {
    const parts = d.ref.path.split("/");
    return { id: d.id, organizationId: parts[1], ...d.data() };
  });
  return docs
    .filter((d) => !stageFilter || d.stage === stageFilter)
    .map((d) => ({ ...d, _score: scoreDoc(loanFields(d), term) }))
    .filter((d) => d._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, MAX_RESULTS_PER_TYPE);
};

const searchDocuments = async (firestore, term) => {
  const ref = collectionGroup(firestore, "documents");
  const q = query(ref, limit(FETCH_LIMIT));
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => {
    const parts = d.ref.path.split("/");
    return { id: d.id, organizationId: parts[1], loanId: parts[3], ...d.data() };
  });
  return docs
    .map((d) => ({ ...d, _score: scoreDoc(docFields(d), term) }))
    .filter((d) => d._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, MAX_RESULTS_PER_TYPE);
};

// ─── UI Helpers ───────────────────────────────────────────────────────────────

const HighlightText = ({ text, term }) => {
  if (!text) return <span>—</span>;
  if (!term) return <span>{text}</span>;
  try {
    // Highlight each token separately
    const tokens = tokenise(term);
    if (!tokens.length) return <span>{text}</span>;
    const pattern = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    const regex = new RegExp(`(${pattern})`, "gi");
    const parts = String(text).split(regex);
    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} style={{ background: "#7F7FD5", color: "#fff", borderRadius: "2px", padding: "0 2px" }}>
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  } catch {
    return <span>{text}</span>;
  }
};

const FieldRow = ({ label, value, term, mono }) => {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div style={{ display: "flex", gap: "8px", marginBottom: "5px", fontSize: "13px", alignItems: "flex-start" }}>
      <span style={{ color: "#888", minWidth: "140px", fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ color: "inherit", fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>
        <HighlightText text={String(value)} term={term} />
      </span>
    </div>
  );
};

const SectionHeader = ({ title, count, isDark }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", marginTop: "4px" }}>
    <h4 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</h4>
    {count != null && (
      <span style={{ fontSize: "11px", padding: "1px 7px", borderRadius: "10px", background: isDark ? "#2d3148" : "#e2e8f0", color: isDark ? "#a0aec0" : "#4a5568" }}>{count}</span>
    )}
  </div>
);

const Divider = ({ isDark }) => (
  <div style={{ height: "1px", background: isDark ? "#2d3148" : "#e2e8f0", margin: "16px 0" }} />
);

const SubBadge = ({ status }) => {
  const colors = {
    active: { bg: "#d4edda", text: "#155724" },
    trialing: { bg: "#fff3cd", text: "#856404" },
    inactive: { bg: "#f8d7da", text: "#721c24" },
    canceled: { bg: "#f8d7da", text: "#721c24" },
    past_due: { bg: "#f8d7da", text: "#721c24" },
  };
  const c = colors[status?.toLowerCase()] || { bg: "#e2e8f0", text: "#4a5568" };
  return (
    <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "12px", background: c.bg, color: c.text, fontWeight: 600, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
};

const StageBadge = ({ stage }) => {
  const colors = {
    Funded: { bg: "#d4edda", text: "#155724" },
    Denied: { bg: "#f8d7da", text: "#721c24" },
    Closing: { bg: "#cce5ff", text: "#004085" },
    Underwriting: { bg: "#fff3cd", text: "#856404" },
    Processing: { bg: "#e2e3e5", text: "#383d41" },
  };
  const c = colors[stage] || { bg: "#dbeafe", text: "#1e40af" };
  return (
    <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "12px", background: c.bg, color: c.text, fontWeight: 600, whiteSpace: "nowrap" }}>
      {stage}
    </span>
  );
};

const NavButton = ({ label, onClick, icon: Icon, isDark }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "6px 12px", fontSize: "12px", fontWeight: 600,
      border: "1px solid #7F7FD5", borderRadius: "6px",
      background: "rgba(127,127,213,0.10)", color: "#7F7FD5",
      cursor: "pointer", whiteSpace: "nowrap",
    }}
  >
    {Icon && <Icon size={11} />}
    {label}
    <FaChevronRight size={9} />
  </button>
);

// ─── Result Cards ─────────────────────────────────────────────────────────────

const cardStyle = (isDark, clickable) => ({
  background: isDark ? "#1e2130" : "#fff",
  border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
  borderRadius: "10px",
  padding: "14px 16px",
  marginBottom: "8px",
  transition: "border-color 0.15s, box-shadow 0.15s",
  cursor: clickable ? "pointer" : "default",
});

const UserResultCard = ({ doc, term, isDark, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const name = doc.name || doc.displayName || doc.firstName ? `${doc.firstName || ""} ${doc.lastName || ""}`.trim() : null;
  return (
    <div
      style={cardStyle(isDark, true)}
      onClick={() => onViewDetails && onViewDetails({ type: "user", id: doc.id, label: doc.email || name || doc.id, context: {} })}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <FaUser style={{ color: "#7F7FD5", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: "14px" }}>
              <HighlightText text={name || doc.email || "Unknown User"} term={term} />
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              <HighlightText text={doc.email} term={term} />
            </div>
          </div>
          {doc.subscriptionStatus && <SubBadge status={doc.subscriptionStatus} />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px" }}
          >
            {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}` }} onClick={(e) => e.stopPropagation()}>
          <FieldRow label="User ID" value={doc.id} term={term} mono />
          <FieldRow label="Email" value={doc.email} term={term} />
          <FieldRow label="Name" value={name} term={term} />
          <FieldRow label="Phone" value={doc.phone || doc.phoneNumber} term={term} />
          <FieldRow label="Organization ID" value={doc.organizationId || doc.defaultForUserId} term={term} mono />
          <FieldRow label="Subscription" value={doc.subscriptionStatus} term={term} />
          <FieldRow label="Plan" value={doc.subscriptionPlan || doc.plan} term={term} />
          <FieldRow label="Account Type" value={doc.accountType} term={term} />
          <FieldRow label="Role" value={doc.role} term={term} />
          <FieldRow label="Created" value={doc.createdAt?.toDate?.()?.toLocaleDateString() || doc.createdAt} term={term} />
        </div>
      )}
    </div>
  );
};

const OrgResultCard = ({ doc, term, isDark, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const label = doc.name || doc.organizationName || doc.companyName || doc.id;
  const subStatus = doc.subscription?.status || doc.subscriptionStatus;
  return (
    <div
      style={cardStyle(isDark, true)}
      onClick={() => onViewDetails && onViewDetails({ type: "org", id: doc.id, label, context: { organizationId: doc.id } })}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <FaBuilding style={{ color: "#86efac", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: "14px" }}>
              <HighlightText text={label} term={term} />
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              <HighlightText text={doc.ownerEmail || doc.email} term={term} />
            </div>
          </div>
          {subStatus && <SubBadge status={subStatus} />}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px" }}
        >
          {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}` }} onClick={(e) => e.stopPropagation()}>
          <FieldRow label="Org ID" value={doc.id} term={term} mono />
          <FieldRow label="Name" value={label} term={term} />
          <FieldRow label="Owner Email" value={doc.ownerEmail || doc.email} term={term} />
          <FieldRow label="Owner User ID" value={doc.defaultForUserId || doc.ownerId} term={term} mono />
          <FieldRow label="Subscription" value={subStatus} term={term} />
          <FieldRow label="Plan" value={doc.subscriptionPlan || doc.subscription?.priceId} term={term} />
          <FieldRow label="Members" value={doc.membersCount} term={term} />
          <FieldRow label="Industry" value={doc.industry} term={term} />
          <FieldRow label="Website" value={doc.website} term={term} />
          <FieldRow label="Created" value={doc.createdAt?.toDate?.()?.toLocaleDateString() || doc.createdAt} term={term} />
        </div>
      )}
    </div>
  );
};

const LoanResultCard = ({ doc, term, isDark, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const label = doc.name || doc.loanNumber || doc.id;
  const borrower = doc.borrowers?.[0];
  const borrowerName = borrower ? `${borrower.firstName || ""} ${borrower.lastName || ""}`.trim() || borrower.email : doc.borrowerName;
  return (
    <div
      style={cardStyle(isDark, true)}
      onClick={() => onViewDetails && onViewDetails({ type: "loan", id: doc.id, label, context: { organizationId: doc.organizationId } })}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <FaFileAlt style={{ color: "#fbbf24", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: "14px" }}>
              <HighlightText text={label} term={term} />
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              <HighlightText text={borrowerName || doc.address || doc.propertyAddress} term={term} />
            </div>
          </div>
          {doc.stage && <StageBadge stage={doc.stage} />}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px" }}
        >
          {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}` }} onClick={(e) => e.stopPropagation()}>
          <FieldRow label="Loan ID" value={doc.id} term={term} mono />
          <FieldRow label="Loan Number" value={doc.loanNumber} term={term} />
          <FieldRow label="LOS ID" value={doc.losId} term={term} />
          <FieldRow label="Borrower" value={borrowerName} term={term} />
          <FieldRow label="Borrower Email" value={borrower?.email || doc.borrowerEmail} term={term} />
          <FieldRow label="Borrower Phone" value={borrower?.phone || borrower?.cellPhone} term={term} />
          <FieldRow label="Property" value={doc.address || doc.propertyAddress} term={term} />
          <FieldRow label="City / State" value={[doc.city, doc.state].filter(Boolean).join(", ")} term={term} />
          <FieldRow label="Loan Amount" value={doc.amount || doc.loanAmount ? `$${Number(doc.amount || doc.loanAmount).toLocaleString()}` : null} term={term} />
          <FieldRow label="Stage" value={doc.stage} term={term} />
          <FieldRow label="Loan Type" value={doc.loanType || doc.purpose} term={term} />
          <FieldRow label="LO Name" value={doc.authorName || doc.loanOfficerName} term={term} />
          <FieldRow label="LO Email" value={doc.authorEmail || doc.loanOfficerEmail} term={term} />
          <FieldRow label="Organization ID" value={doc.organizationId} term={term} mono />
          <FieldRow label="Created" value={doc.createdAt?.toDate?.()?.toLocaleDateString() || doc.createdAt} term={term} />
        </div>
      )}
    </div>
  );
};

const DocumentResultCard = ({ doc, term, isDark, onViewDetails }) => {
  const [expanded, setExpanded] = useState(false);
  const label = doc.name || doc.originalName || doc.fileName || doc.id;
  return (
    <div
      style={cardStyle(isDark, true)}
      onClick={() => onViewDetails && onViewDetails({ type: "document", id: doc.id, label, context: { organizationId: doc.organizationId, loanId: doc.loanId } })}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <FaFileAlt style={{ color: "#a78bfa", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: "14px" }}>
              <HighlightText text={label} term={term} />
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              <HighlightText text={[doc.type, doc.classification].filter(Boolean).join(" · ") || doc.uploadedBy} term={term} />
            </div>
          </div>
          {doc.status && (
            <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "12px", background: "#e2e3e5", color: "#383d41", fontWeight: 600 }}>{doc.status}</span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "4px" }}
        >
          {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}` }} onClick={(e) => e.stopPropagation()}>
          <FieldRow label="Doc ID" value={doc.id} term={term} mono />
          <FieldRow label="Name" value={label} term={term} />
          <FieldRow label="Type" value={doc.type} term={term} />
          <FieldRow label="Classification" value={doc.classification} term={term} />
          <FieldRow label="Group" value={doc.group} term={term} />
          <FieldRow label="Status" value={doc.status} term={term} />
          <FieldRow label="Uploaded By" value={doc.uploadedBy} term={term} />
          <FieldRow label="Borrower" value={[doc.borrowerFirstName, doc.borrowerLastName].filter(Boolean).join(" ")} term={term} />
          <FieldRow label="Loan ID" value={doc.loanId} term={term} mono />
          <FieldRow label="Organization ID" value={doc.organizationId} term={term} mono />
          <FieldRow label="Created" value={doc.createdAt?.toDate?.()?.toLocaleDateString() || doc.uploadedAt?.toDate?.()?.toLocaleDateString() || doc.createdAt} term={term} />
        </div>
      )}
    </div>
  );
};

// ─── Results Section ──────────────────────────────────────────────────────────

const ResultsSection = ({ title, icon: Icon, color, results, term, isDark, CardComponent, onViewDetails }) => {
  if (!results || results.length === 0) return null;
  return (
    <div style={{ marginBottom: "28px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <Icon style={{ color }} />
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: isDark ? "#e2e8f0" : "#1a202c" }}>{title}</h3>
        <span style={{ fontSize: "12px", padding: "1px 8px", borderRadius: "12px", background: isDark ? "#2d3148" : "#e2e8f0", color: isDark ? "#a0aec0" : "#4a5568" }}>
          {results.length}
        </span>
        <span style={{ fontSize: "11px", color: "#888", marginLeft: "4px" }}>Click any row to open details</span>
      </div>
      {results.map((d) => (
        <CardComponent key={d.id} doc={d} term={term} isDark={isDark} onViewDetails={onViewDetails} />
      ))}
    </div>
  );
};

// ─── Detail Panels ────────────────────────────────────────────────────────────

const DetailPanelShell = ({ title, subtitle, icon: Icon, isDark, onBack, breadcrumbs, children }) => {
  const panelStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
  };
  return (
    <div style={panelStyle}>
      {/* Breadcrumb trail */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "16px", flexWrap: "wrap", fontSize: "12px", color: "#888" }}>
          <FaHome size={11} />
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              <FaChevronRight size={9} style={{ opacity: 0.5 }} />
              <span
                onClick={b.onClick}
                style={{ cursor: b.onClick ? "pointer" : "default", color: b.onClick ? "#7F7FD5" : "#888", fontWeight: i === breadcrumbs.length - 1 ? 700 : 400 }}
              >
                {b.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", fontSize: "14px", fontWeight: 600,
            border: `1px solid ${isDark ? "#3d4466" : "#cbd5e0"}`, borderRadius: "8px",
            background: isDark ? "#1e2130" : "#f7f9fc", color: isDark ? "#e2e8f0" : "#1a202c", cursor: "pointer",
          }}
        >
          <FaArrowLeft size={12} /> Back
        </button>
        {Icon && <Icon style={{ color: "#7F7FD5", fontSize: "20px" }} />}
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: isDark ? "#e2e8f0" : "#1a202c" }}>{title}</h2>
          {subtitle && <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
};

const InfoGrid = ({ items, isDark }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px", fontSize: "13px" }}>
    {items.filter((item) => item.value != null && item.value !== "" && item.value !== "—").map(({ label, value, mono }) => (
      <div key={label} style={{ padding: "10px 12px", background: isDark ? "#1e2130" : "#f7f9fc", borderRadius: "8px", border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}` }}>
        <div style={{ color: "#888", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>{label}</div>
        <div style={{ fontWeight: 600, wordBreak: "break-all", fontFamily: mono ? "monospace" : "inherit", fontSize: mono ? "11px" : "13px" }}>{String(value)}</div>
      </div>
    ))}
  </div>
);

const TableSection = ({ title, icon: Icon, rows, columns, isDark, emptyText }) => {
  const tableBorder = isDark ? "#2d3148" : "#e2e8f0";
  return (
    <div style={{ marginBottom: "24px" }}>
      <SectionHeader title={title} count={rows.length} isDark={isDark} />
      {rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>{emptyText || "None found."}</p>
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${tableBorder}`, borderRadius: "8px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${tableBorder}`, background: isDark ? "#1a1f32" : "#f7f9fc" }}>
                {columns.map((c) => (
                  <th key={c.key} style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#888", whiteSpace: "nowrap" }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ padding: "10px 12px", verticalAlign: "top" }}>
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Org Detail Panel ─────────────────────────────────────────────────────────

const OrgDetailPanel = ({ firestore, organizationId, isDark, onBack, onNavigate, breadcrumbs }) => {
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [orgSnap, membersSnap, loansSnap] = await Promise.all([
          getDoc(doc(firestore, "organizations", organizationId)),
          getDocs(query(collection(firestore, "organization-members"), where("organizationId", "==", organizationId))).catch(() => ({ docs: [] })),
          getDocs(query(collectionGroup(firestore, "loans"), where("organizationId", "==", organizationId), limit(100))).catch(() => ({ docs: [] })),
        ]);
        if (cancelled) return;
        setOrg(orgSnap.exists() ? { id: orgSnap.id, ...orgSnap.data() } : null);
        setMembers(membersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoans(loansSnap.docs.map((d) => ({ id: d.id, organizationId, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firestore, organizationId]);

  if (loading) return <DetailPanelShell title="Organization" icon={FaBuilding} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ color: "#888", padding: "24px 0", textAlign: "center" }}><FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: "24px", color: "#7F7FD5" }} /></div></DetailPanelShell>;
  if (!org) return <DetailPanelShell title="Organization" icon={FaBuilding} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ color: "#888" }}>Organization not found.</div></DetailPanelShell>;

  const sub = org.subscription || {};
  const orgName = org.name || org.organizationName || org.companyName || org.id;

  return (
    <DetailPanelShell
      title={orgName}
      subtitle={`ID: ${org.id}`}
      icon={FaBuilding}
      isDark={isDark}
      onBack={onBack}
      breadcrumbs={breadcrumbs}
    >
      {/* Core info */}
      <SectionHeader title="Organization details" isDark={isDark} />
      <InfoGrid isDark={isDark} items={[
        { label: "Org ID", value: org.id, mono: true },
        { label: "Name", value: orgName },
        { label: "Owner Email", value: org.ownerEmail || org.email },
        { label: "Owner User ID", value: org.defaultForUserId || org.ownerId, mono: true },
        { label: "Industry", value: org.industry },
        { label: "Website", value: org.website },
        { label: "Members Count", value: org.membersCount },
        { label: "Created", value: org.createdAt?.toDate?.()?.toLocaleDateString() || org.createdAt },
      ]} />

      <Divider isDark={isDark} />

      {/* Subscription / Payment */}
      <SectionHeader title="Subscription & billing" icon={FaCreditCard} isDark={isDark} />
      <InfoGrid isDark={isDark} items={[
        { label: "Status", value: sub.status || org.subscriptionStatus },
        { label: "Plan / Price ID", value: org.subscriptionPlan || sub.priceId },
        { label: "Stripe Customer ID", value: sub.customerId, mono: true },
        { label: "Stripe Subscription ID", value: sub.subscriptionId || sub.id, mono: true },
        { label: "Current Period End", value: sub.currentPeriodEnd?.toDate?.()?.toLocaleDateString() || (sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd * 1000).toLocaleDateString() : null) },
        { label: "Trial End", value: sub.trialEnd?.toDate?.()?.toLocaleDateString() || (sub.trialEnd ? new Date(sub.trialEnd * 1000).toLocaleDateString() : null) },
        { label: "Cancel At Period End", value: sub.cancelAtPeriodEnd != null ? String(sub.cancelAtPeriodEnd) : null },
        { label: "Quantity", value: sub.quantity },
      ]} />

      <Divider isDark={isDark} />

      {/* Members */}
      <TableSection
        title="Members"
        rows={members}
        isDark={isDark}
        emptyText="No members found."
        columns={[
          { key: "email", label: "Email" },
          { key: "role", label: "Role" },
          { key: "status", label: "Status" },
          { key: "isAddy", label: "Is Addy", render: (m) => m.isAddy ? "Yes" : "No" },
          { key: "action", label: "", render: (m) => m.userId ? (
            <NavButton label="View user" onClick={() => onNavigate({ type: "user", id: m.userId, label: m.email || m.userId, context: {} })} />
          ) : null },
        ]}
      />

      {/* Loans */}
      <TableSection
        title="Loans"
        rows={loans}
        isDark={isDark}
        emptyText="No loans found."
        columns={[
          { key: "loanNumber", label: "Loan #", render: (l) => l.loanNumber || l.name || l.id },
          { key: "borrower", label: "Borrower", render: (l) => l.borrowers?.[0] ? `${l.borrowers[0].firstName || ""} ${l.borrowers[0].lastName || ""}`.trim() || l.borrowers[0].email : l.borrowerName || "—" },
          { key: "address", label: "Address", render: (l) => l.address || l.propertyAddress || "—" },
          { key: "stage", label: "Stage", render: (l) => l.stage ? <StageBadge stage={l.stage} /> : "—" },
          { key: "action", label: "", render: (l) => (
            <NavButton label="View loan" onClick={() => onNavigate({ type: "loan", id: l.id, label: l.loanNumber || l.id, context: { organizationId } })} />
          )},
        ]}
      />
    </DetailPanelShell>
  );
};

// ─── User Detail Panel ────────────────────────────────────────────────────────

const UserDetailPanel = ({ firestore, userId, isDark, onBack, onNavigate, breadcrumbs }) => {
  const [user, setUser] = useState(null);
  const [ownedOrg, setOwnedOrg] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userSnap = await getDoc(doc(firestore, "users", userId));
        if (!userSnap.exists()) { if (!cancelled) setLoading(false); return; }
        const userData = { id: userSnap.id, ...userSnap.data() };
        if (cancelled) return;
        setUser(userData);
        const [ownedSnap, membersSnap] = await Promise.all([
          getDocs(query(collection(firestore, "organizations"), where("defaultForUserId", "==", userId), limit(1))).catch(() => ({ docs: [] })),
          getDocs(query(collection(firestore, "organization-members"), where("userId", "==", userId))).catch(() => ({ docs: [] })),
        ]);
        if (cancelled) return;
        setOwnedOrg(ownedSnap.docs[0] ? { id: ownedSnap.docs[0].id, ...ownedSnap.docs[0].data() } : null);
        setMemberships(membersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firestore, userId]);

  if (loading) return <DetailPanelShell title="User" icon={FaUser} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ textAlign: "center", padding: "24px 0" }}><FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: "24px", color: "#7F7FD5" }} /></div></DetailPanelShell>;
  if (!user) return <DetailPanelShell title="User" icon={FaUser} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ color: "#888" }}>User not found.</div></DetailPanelShell>;

  const name = user.name || user.displayName || [user.firstName, user.lastName].filter(Boolean).join(" ");

  return (
    <DetailPanelShell
      title={name || user.email || user.id}
      subtitle={user.email}
      icon={FaUser}
      isDark={isDark}
      onBack={onBack}
      breadcrumbs={breadcrumbs}
    >
      <SectionHeader title="User details" isDark={isDark} />
      <InfoGrid isDark={isDark} items={[
        { label: "User ID", value: user.id, mono: true },
        { label: "Email", value: user.email },
        { label: "Name", value: name },
        { label: "Phone", value: user.phone || user.phoneNumber },
        { label: "Account Type", value: user.accountType },
        { label: "Role", value: user.role },
        { label: "Subscription", value: user.subscriptionStatus },
        { label: "Plan", value: user.subscriptionPlan || user.plan },
        { label: "Organization ID", value: user.organizationId || user.defaultForUserId, mono: true },
        { label: "Created", value: user.createdAt?.toDate?.()?.toLocaleDateString() || user.createdAt },
      ]} />

      {ownedOrg && (
        <>
          <Divider isDark={isDark} />
          <SectionHeader title="Owned organization" isDark={isDark} />
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>{ownedOrg.name || ownedOrg.organizationName || ownedOrg.id}</div>
            {(ownedOrg.subscription?.status || ownedOrg.subscriptionStatus) && <SubBadge status={ownedOrg.subscription?.status || ownedOrg.subscriptionStatus} />}
            <NavButton label="View organization" onClick={() => onNavigate({ type: "org", id: ownedOrg.id, label: ownedOrg.name || ownedOrg.id, context: { organizationId: ownedOrg.id } })} />
          </div>
        </>
      )}

      {memberships.length > 0 && (
        <>
          <Divider isDark={isDark} />
          <TableSection
            title="Organization memberships"
            rows={memberships}
            isDark={isDark}
            columns={[
              { key: "organizationId", label: "Organization ID" },
              { key: "role", label: "Role" },
              { key: "status", label: "Status" },
              { key: "action", label: "", render: (m) => (
                <NavButton label="View org" onClick={() => onNavigate({ type: "org", id: m.organizationId, label: m.organizationId, context: { organizationId: m.organizationId } })} />
              )},
            ]}
          />
        </>
      )}
    </DetailPanelShell>
  );
};

// ─── Loan Detail Panel ────────────────────────────────────────────────────────

const LoanDetailPanel = ({ firestore, loanId, organizationId, isDark, onBack, onNavigate, breadcrumbs }) => {
  const [loan, setLoan] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [pcTasks, setPcTasks] = useState([]);
  const [queueLogs, setQueueLogs] = useState([]);
  const [showQueueLogs, setShowQueueLogs] = useState(false);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const loanRef = doc(firestore, "organizations", organizationId, "loans", loanId);
        const [loanSnap, docsSnap, tasksSnap, orgSnap] = await Promise.all([
          getDoc(loanRef),
          getDocs(collection(firestore, "organizations", organizationId, "loans", loanId, "documents")).catch(() => ({ docs: [] })),
          getDocs(collection(firestore, "organizations", organizationId, "loans", loanId, "processingChecklistTasks")).catch(() => ({ docs: [] })),
          getDoc(doc(firestore, "organizations", organizationId)).catch(() => null),
        ]);
        if (cancelled) return;
        setLoan(loanSnap.exists() ? { id: loanSnap.id, ...loanSnap.data() } : null);
        setDocuments(docsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPcTasks(tasksSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        if (orgSnap?.exists()) setOrg({ id: orgSnap.id, ...orgSnap.data() });
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firestore, organizationId, loanId]);

  const fetchQueueLogs = useCallback(async () => {
    if (!organizationId || !loanId) return;
    try {
      const logsSnap = await getDocs(query(collection(firestore, "queue-logs"), where("organizationId", "==", organizationId), limit(200)));
      const list = logsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setQueueLogs(list.filter((log) => {
        try {
          const body = typeof log.body === "string" ? JSON.parse(log.body || "{}") : log.body || {};
          return body.loanId === loanId || body.payload?.loanId === loanId || String(log.body || "").includes(loanId);
        } catch {
          return String(log.body || "").includes(loanId);
        }
      }));
    } catch (err) {
      console.error(err);
    }
  }, [firestore, organizationId, loanId]);

  if (loading) return <DetailPanelShell title="Loan" icon={FaFileAlt} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ textAlign: "center", padding: "24px 0" }}><FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: "24px", color: "#7F7FD5" }} /></div></DetailPanelShell>;
  if (!loan) return <DetailPanelShell title="Loan" icon={FaFileAlt} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ color: "#888" }}>Loan not found.</div></DetailPanelShell>;

  const summary = loan.processingChecklistSummary || {};
  const borrower = loan.borrowers?.[0];
  const borrowerName = borrower ? `${borrower.firstName || ""} ${borrower.lastName || ""}`.trim() : null;
  const appUrl = `https://app.addy.so/loans?tab=board&loan=${loanId}&tabValue=processingChecklist`;
  const orgName = org ? (org.name || org.organizationName || org.id) : organizationId;

  return (
    <DetailPanelShell
      title={loan.name || loan.loanNumber || loan.id}
      subtitle={borrowerName ? `Borrower: ${borrowerName}` : null}
      icon={FaFileAlt}
      isDark={isDark}
      onBack={onBack}
      breadcrumbs={breadcrumbs}
    >
      {/* Quick actions */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        <a href={appUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#7F7FD5", fontSize: "13px", fontWeight: 600 }}>
          Open in app <FaExternalLinkAlt size={11} />
        </a>
        {org && (
          <NavButton
            label={`View org: ${orgName}`}
            icon={FaBuilding}
            onClick={() => onNavigate({ type: "org", id: organizationId, label: orgName, context: { organizationId } })}
          />
        )}
      </div>

      {/* Loan details */}
      <SectionHeader title="Loan details" isDark={isDark} />
      <InfoGrid isDark={isDark} items={[
        { label: "Loan ID", value: loan.id, mono: true },
        { label: "Loan Number", value: loan.loanNumber },
        { label: "LOS ID", value: loan.losId },
        { label: "Stage", value: loan.stage },
        { label: "Loan Type", value: loan.loanType || loan.purpose },
        { label: "Amount", value: loan.amount != null ? `$${Number(loan.amount).toLocaleString()}` : null },
        { label: "Address", value: loan.address || loan.propertyAddress },
        { label: "City / State / Zip", value: [loan.city, loan.state, loan.zipCode].filter(Boolean).join(", ") },
        { label: "LO Name", value: loan.authorName || loan.loanOfficerName },
        { label: "LO Email", value: loan.authorEmail || loan.loanOfficerEmail },
        { label: "Organization ID", value: loan.organizationId, mono: true },
        { label: "Created", value: loan.createdAt?.toDate?.()?.toLocaleDateString() || loan.createdAt },
      ]} />

      {/* Borrowers */}
      {loan.borrowers && loan.borrowers.length > 0 && (
        <>
          <Divider isDark={isDark} />
          <TableSection
            title="Borrowers"
            rows={loan.borrowers}
            isDark={isDark}
            columns={[
              { key: "name", label: "Name", render: (b) => `${b.firstName || ""} ${b.lastName || ""}`.trim() || b.name || "—" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone", render: (b) => b.phone || b.cellPhone || b.workPhone || "—" },
            ]}
          />
        </>
      )}

      {/* Processing checklist summary */}
      {(summary.pending != null || summary.completed != null) && (
        <>
          <Divider isDark={isDark} />
          <SectionHeader title="Processing checklist summary" isDark={isDark} />
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {[
              { label: "Pending", value: summary.pending, color: "#fbbf24" },
              { label: "Attention", value: summary.attention, color: "#f87171" },
              { label: "Processing", value: summary.processing, color: "#60a5fa" },
              { label: "Completed", value: summary.completed, color: "#34d399" },
              { label: "Hidden", value: summary.hidden, color: "#9ca3af" },
            ].filter((s) => s.value != null).map((s) => (
              <div key={s.label} style={{ padding: "10px 16px", borderRadius: "8px", background: isDark ? "#1e2130" : "#f7f9fc", border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`, textAlign: "center" }}>
                <div style={{ fontSize: "20px", fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* PC Tasks */}
      {pcTasks.length > 0 && (
        <>
          <Divider isDark={isDark} />
          <TableSection
            title="Processing checklist tasks"
            rows={pcTasks}
            isDark={isDark}
            columns={[
              { key: "title", label: "Title", render: (t) => t.title || t.id },
              { key: "status", label: "Status" },
              { key: "category", label: "Category" },
            ]}
          />
        </>
      )}

      {/* Documents */}
      <Divider isDark={isDark} />
      <TableSection
        title="Documents"
        rows={documents}
        isDark={isDark}
        emptyText="No documents found."
        columns={[
          { key: "name", label: "Name", render: (d) => d.name || d.originalName || d.id },
          { key: "type", label: "Type / Classification", render: (d) => [d.type, d.classification].filter(Boolean).join(" · ") || "—" },
          { key: "status", label: "Status" },
          { key: "action", label: "", render: (d) => (
            <NavButton label="View doc" onClick={() => onNavigate({ type: "document", id: d.id, label: d.name || d.id, context: { organizationId, loanId } })} />
          )},
        ]}
      />

      {/* Queue logs */}
      <div>
        <button
          type="button"
          onClick={() => { setShowQueueLogs((v) => !v); if (!showQueueLogs) fetchQueueLogs(); }}
          style={{ padding: "8px 14px", fontSize: "13px", border: "1px solid #7F7FD5", borderRadius: "6px", background: "rgba(127,127,213,0.10)", color: "#7F7FD5", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
        >
          <FaList size={12} /> {showQueueLogs ? "Hide" : "Show"} queue logs
        </button>
        {showQueueLogs && (
          <div style={{ marginTop: "12px", border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`, borderRadius: "8px", padding: "12px", maxHeight: "300px", overflow: "auto" }}>
            {queueLogs.length === 0 ? (
              <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>No matching queue logs.</p>
            ) : (
              queueLogs.map((log) => (
                <div key={log.id} style={{ marginBottom: "8px", padding: "8px", background: isDark ? "#1e2130" : "#f7f9fc", borderRadius: "6px", fontSize: "12px" }}>
                  <div><strong>{log.queueId}</strong> — {log.success ? "✅ Success" : "❌ Failed"} — {log.createdAt?.toDate?.()?.toLocaleString() || log.id}</div>
                  {log.body && <div style={{ color: "#888", marginTop: "4px", wordBreak: "break-all" }}>{typeof log.body === "string" ? log.body.slice(0, 300) : JSON.stringify(log.body).slice(0, 300)}…</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </DetailPanelShell>
  );
};

// ─── Document Detail Panel ────────────────────────────────────────────────────

const DocumentDetailPanel = ({ firestore, documentId, organizationId, loanId, isDark, onBack, onNavigate, breadcrumbs }) => {
  const [document, setDocument] = useState(null);
  const [loan, setLoan] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId || !loanId) return;
    let cancelled = false;
    (async () => {
      try {
        const docRef = doc(firestore, "organizations", organizationId, "loans", loanId, "documents", documentId);
        const loanRef = doc(firestore, "organizations", organizationId, "loans", loanId);
        const orgRef = doc(firestore, "organizations", organizationId);
        const [docSnap, loanSnap, orgSnap] = await Promise.all([getDoc(docRef), getDoc(loanRef), getDoc(orgRef)]);
        if (cancelled) return;
        setDocument(docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null);
        setLoan(loanSnap.exists() ? { id: loanSnap.id, ...loanSnap.data() } : null);
        setOrg(orgSnap.exists() ? { id: orgSnap.id, ...orgSnap.data() } : null);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firestore, organizationId, loanId, documentId]);

  if (loading) return <DetailPanelShell title="Document" icon={FaFileAlt} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ textAlign: "center", padding: "24px 0" }}><FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: "24px", color: "#7F7FD5" }} /></div></DetailPanelShell>;
  if (!document) return <DetailPanelShell title="Document" icon={FaFileAlt} isDark={isDark} onBack={onBack} breadcrumbs={breadcrumbs}><div style={{ color: "#888" }}>Document not found.</div></DetailPanelShell>;

  const orgName = org ? (org.name || org.organizationName || org.id) : organizationId;
  const loanLabel = loan ? (loan.loanNumber || loan.name || loan.id) : loanId;

  return (
    <DetailPanelShell
      title={document.name || document.originalName || document.id}
      subtitle={`${document.type || document.classification || "Document"}`}
      icon={FaFileAlt}
      isDark={isDark}
      onBack={onBack}
      breadcrumbs={breadcrumbs}
    >
      {/* Navigation chain: Doc → Loan → Org */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        {loan && (
          <NavButton
            label={`Parent loan: ${loanLabel}`}
            icon={FaFileAlt}
            onClick={() => onNavigate({ type: "loan", id: loan.id, label: loanLabel, context: { organizationId } })}
          />
        )}
        {org && (
          <NavButton
            label={`Organization: ${orgName}`}
            icon={FaBuilding}
            onClick={() => onNavigate({ type: "org", id: org.id, label: orgName, context: { organizationId } })}
          />
        )}
      </div>

      <SectionHeader title="Document details" isDark={isDark} />
      <InfoGrid isDark={isDark} items={[
        { label: "Doc ID", value: document.id, mono: true },
        { label: "Name", value: document.name || document.originalName || document.fileName },
        { label: "Type", value: document.type },
        { label: "Classification", value: document.classification },
        { label: "Group", value: document.group },
        { label: "Status", value: document.status },
        { label: "Uploaded By", value: document.uploadedBy },
        { label: "Borrower", value: [document.borrowerFirstName, document.borrowerLastName].filter(Boolean).join(" ") },
        { label: "Not Extracted", value: document.notExtracted != null ? String(document.notExtracted) : null },
        { label: "Text Extraction", value: document.textExtractionProvider },
        { label: "Ingested At", value: document.ingestedAt?.toDate?.()?.toLocaleString() },
        { label: "Loan ID", value: document.loanId || loanId, mono: true },
        { label: "Organization ID", value: document.organizationId || organizationId, mono: true },
        { label: "Created", value: document.createdAt?.toDate?.()?.toLocaleDateString() || document.uploadedAt?.toDate?.()?.toLocaleDateString() || document.createdAt },
      ]} />

      {/* Loan summary */}
      {loan && (
        <>
          <Divider isDark={isDark} />
          <SectionHeader title="Parent loan summary" isDark={isDark} />
          <InfoGrid isDark={isDark} items={[
            { label: "Loan Number", value: loan.loanNumber },
            { label: "Stage", value: loan.stage },
            { label: "Borrower", value: loan.borrowers?.[0] ? `${loan.borrowers[0].firstName || ""} ${loan.borrowers[0].lastName || ""}`.trim() : null },
            { label: "Address", value: loan.address || loan.propertyAddress },
            { label: "Amount", value: loan.amount != null ? `$${Number(loan.amount).toLocaleString()}` : null },
            { label: "LO", value: loan.authorName || loan.loanOfficerName },
          ]} />
        </>
      )}

      {/* Org summary */}
      {org && (
        <>
          <Divider isDark={isDark} />
          <SectionHeader title="Organization summary" isDark={isDark} />
          <InfoGrid isDark={isDark} items={[
            { label: "Org Name", value: orgName },
            { label: "Owner Email", value: org.ownerEmail || org.email },
            { label: "Subscription", value: org.subscription?.status || org.subscriptionStatus },
            { label: "Plan", value: org.subscriptionPlan || org.subscription?.priceId },
          ]} />
        </>
      )}
    </DetailPanelShell>
  );
};

// ─── Breadcrumb helpers ───────────────────────────────────────────────────────

const buildBreadcrumbs = (stack, goToIndex) => {
  if (!stack || stack.length === 0) return [];
  return [
    { label: "Search results", onClick: () => goToIndex(-1) },
    ...stack.map((item, i) => ({
      label: item.label || item.id,
      onClick: i < stack.length - 1 ? () => goToIndex(i) : null,
    })),
  ];
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const GlobalSearchPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [environment, setEnvironment] = useState("prod");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [breadcrumbStack, setBreadcrumbStack] = useState([]);
  const [theme] = useDarkMode();
  const isDark = theme === "dark";
  const inputRef = useRef(null);

  const firestore = environment === "prod" ? firestoreProd : firestoreDev;

  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [naturalLanguageMode, setNaturalLanguageMode] = useState(false);

  const runSearch = useCallback(async (term, type, overrides = {}) => {
    const trimmed = (term || "").trim();
    const subF = overrides.subscriptionFilter ?? subscriptionFilter;
    const stgF = overrides.stageFilter ?? stageFilter;
    if (!trimmed && !subF && !stgF) return;

    setIsLoading(true);
    setActiveSearchTerm(trimmed);
    setResults(null);

    try {
      const searches = {};
      if (type === "users" || type === "all") searches.users = searchUsers(firestore, trimmed);
      if (type === "organizations" || type === "all") searches.organizations = searchOrganizations(firestore, trimmed, subF || undefined);
      if (type === "loans" || type === "all") searches.loans = searchLoans(firestore, trimmed, stgF || undefined);
      if (type === "documents" || type === "all") searches.documents = searchDocuments(firestore, trimmed);

      const keys = Object.keys(searches);
      const values = await Promise.all(Object.values(searches));
      const resolved = {};
      keys.forEach((k, i) => { resolved[k] = values[i]; });
      setResults(resolved);

      const total = Object.values(resolved).reduce((sum, arr) => sum + arr.length, 0);
      if (total === 0) toast.info(`No results found for "${trimmed || "filters"}"`);
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Search failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [firestore, subscriptionFilter, stageFilter]);

  const handleSubmitSearch = useCallback(async () => {
    if (naturalLanguageMode && searchTerm.trim()) {
      setIsLoading(true);
      const base = environment === "prod" ? apiUrl : window.location.hostname === "localhost" ? apiUrlLocal : apiUrlDev;
      try {
        const res = await fetch(`${base}/admin/global-search-parse-query`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: searchTerm.trim() }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          toast.error(data.reason || "Failed to understand your question.");
          setIsLoading(false);
          return;
        }
        runSearch(data.term || "", data.searchType || "all", {
          subscriptionFilter: data.subscriptionFilter || "",
          stageFilter: data.stageFilter || "",
        });
      } catch (err) {
        console.error("Parse query error:", err);
        toast.error("Could not reach the server. Try again or use standard search.");
        setIsLoading(false);
      }
      return;
    }
    runSearch(searchTerm, searchType);
  }, [naturalLanguageMode, searchTerm, searchType, environment, runSearch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmitSearch();
  };

  const handleClear = () => {
    setSearchTerm("");
    setResults(null);
    setActiveSearchTerm("");
    inputRef.current?.focus();
  };

  const navigateToDetail = useCallback((item) => {
    setBreadcrumbStack((prev) => [...prev, item]);
  }, []);

  const goBack = useCallback(() => {
    setBreadcrumbStack((prev) => prev.slice(0, -1));
  }, []);

  const goToIndex = useCallback((index) => {
    if (index < 0) setBreadcrumbStack([]);
    else setBreadcrumbStack((prev) => prev.slice(0, index + 1));
  }, []);

  const currentDetail = breadcrumbStack.length > 0 ? breadcrumbStack[breadcrumbStack.length - 1] : null;
  const breadcrumbs = buildBreadcrumbs(breadcrumbStack, goToIndex);

  const totalResults = results ? Object.values(results).reduce((sum, arr) => sum + arr.length, 0) : 0;

  // ── Styles ───────────────────────────────────────────────────────────────────

  const cardPanelStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
  };

  const inputStyle = {
    flex: 1,
    padding: "12px 16px",
    fontSize: "15px",
    border: `1.5px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
    borderRadius: "8px",
    background: isDark ? "#1e2130" : "#f7f9fc",
    color: isDark ? "#e2e8f0" : "#1a202c",
    outline: "none",
    transition: "border-color 0.15s",
  };

  const selectStyle = {
    padding: "12px 14px",
    fontSize: "14px",
    border: `1.5px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
    borderRadius: "8px",
    background: isDark ? "#1e2130" : "#f7f9fc",
    color: isDark ? "#e2e8f0" : "#1a202c",
    cursor: "pointer",
    outline: "none",
    minWidth: "160px",
  };

  const envToggleStyle = (env) => ({
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    background: environment === env ? "#7F7FD5" : (isDark ? "#2d3148" : "#e2e8f0"),
    color: environment === env ? "#fff" : (isDark ? "#a0aec0" : "#4a5568"),
    transition: "all 0.15s",
  });

  const searchBtnStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    cursor: isLoading ? "not-allowed" : "pointer",
    background: isLoading ? "#9ca3af" : "#7F7FD5",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  };

  return (
    <div className="container-fluid p-4 GlobalSearchPageContainer" style={{ color: isDark ? "#e2e8f0" : "#1a202c", fontFamily: "'Inter', 'Segoe UI', sans-serif", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      {/* Detail view */}
      {currentDetail && (
        <>
          {currentDetail.type === "org" && <OrgDetailPanel firestore={firestore} organizationId={currentDetail.id} isDark={isDark} onBack={goBack} onNavigate={navigateToDetail} breadcrumbs={breadcrumbs} />}
          {currentDetail.type === "user" && <UserDetailPanel firestore={firestore} userId={currentDetail.id} isDark={isDark} onBack={goBack} onNavigate={navigateToDetail} breadcrumbs={breadcrumbs} />}
          {currentDetail.type === "loan" && <LoanDetailPanel firestore={firestore} loanId={currentDetail.id} organizationId={currentDetail.context?.organizationId} isDark={isDark} onBack={goBack} onNavigate={navigateToDetail} breadcrumbs={breadcrumbs} />}
          {currentDetail.type === "document" && <DocumentDetailPanel firestore={firestore} documentId={currentDetail.id} organizationId={currentDetail.context?.organizationId} loanId={currentDetail.context?.loanId} isDark={isDark} onBack={goBack} onNavigate={navigateToDetail} breadcrumbs={breadcrumbs} />}
        </>
      )}

      {/* Search UI */}
      {!currentDetail && (
        <>
          {/* Header */}
          <div style={{ marginBottom: "28px" }}>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: 700, display: "flex", alignItems: "center", gap: "10px" }}>
              <FaSearch style={{ color: "#7F7FD5" }} />
              Global Search
            </h1>
            <p style={{ margin: "6px 0 0", color: "#888", fontSize: "14px" }}>
              Search across users, organizations, loans, and documents. Press{" "}
              <kbd style={{ background: isDark ? "#2d3148" : "#e2e8f0", padding: "1px 6px", borderRadius: "4px", fontSize: "12px" }}>Enter</kbd>{" "}
              to search. Click any result card to drill into details.
            </p>
          </div>

          {/* Search Panel */}
          <div style={cardPanelStyle}>
            {/* Environment + NL toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FaDatabase style={{ color: "#888", fontSize: "13px" }} />
                <span style={{ fontSize: "13px", color: "#888" }}>Environment:</span>
                <button style={envToggleStyle("prod")} onClick={() => setEnvironment("prod")}>Production</button>
                <button style={envToggleStyle("dev")} onClick={() => setEnvironment("dev")}>Development</button>
              </div>
              <button
                type="button"
                onClick={() => setNaturalLanguageMode((v) => !v)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "6px 14px", fontSize: "12px", fontWeight: 600,
                  border: `1.5px solid ${naturalLanguageMode ? "#7F7FD5" : (isDark ? "#2d3148" : "#e2e8f0")}`,
                  borderRadius: "20px", cursor: "pointer",
                  background: naturalLanguageMode ? "rgba(127,127,213,0.15)" : "transparent",
                  color: naturalLanguageMode ? "#7F7FD5" : (isDark ? "#a0aec0" : "#4a5568"),
                  transition: "all 0.15s",
                }}
                title="Ask in plain language; AI will pick search type and filters"
              >
                ✨ Natural language {naturalLanguageMode ? "ON" : "OFF"}
              </button>
            </div>

            {/* Search Input Row */}
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              {!naturalLanguageMode && (
                <select value={searchType} onChange={(e) => setSearchType(e.target.value)} style={selectStyle}>
                  {SEARCH_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              )}

              <div style={{ flex: 1, position: "relative", minWidth: "200px" }}>
                <FaSearch style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: "13px", pointerEvents: "none" }} />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    naturalLanguageMode
                      ? "Ask anything — e.g. \"Loans for John Smith\", \"Orgs on trial\", \"Find user jay@example.com\""
                      : `Search by ${SEARCH_TYPES.find((t) => t.value === searchType)?.description || "..."}  — press Enter`
                  }
                  style={{ ...inputStyle, paddingLeft: "38px", paddingRight: searchTerm ? "38px" : "16px", width: "100%", boxSizing: "border-box" }}
                  autoFocus
                />
                {searchTerm && (
                  <FaTimes onClick={handleClear} style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#888", cursor: "pointer", fontSize: "13px" }} />
                )}
              </div>

              <button
                onClick={handleSubmitSearch}
                disabled={isLoading || (!naturalLanguageMode && !searchTerm.trim() && !subscriptionFilter && !stageFilter)}
                style={searchBtnStyle}
              >
                {isLoading ? (
                  <><FaSpinner style={{ animation: "spin 1s linear infinite" }} /> Searching…</>
                ) : (
                  <><FaSearch /> Search</>
                )}
              </button>
            </div>

            {/* Type chips (standard mode only) */}
            {!naturalLanguageMode && (
              <div style={{ display: "flex", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
                {SEARCH_TYPES.map((t) => {
                  const Icon = t.icon;
                  const isActive = searchType === t.value;
                  return (
                    <button
                      key={t.value}
                      onClick={() => setSearchType(t.value)}
                      style={{
                        display: "flex", alignItems: "center", gap: "6px",
                        padding: "6px 12px", fontSize: "12px", fontWeight: 600,
                        border: `1.5px solid ${isActive ? "#7F7FD5" : (isDark ? "#2d3148" : "#e2e8f0")}`,
                        borderRadius: "20px", cursor: "pointer",
                        background: isActive ? "rgba(127,127,213,0.12)" : "transparent",
                        color: isActive ? "#7F7FD5" : (isDark ? "#a0aec0" : "#4a5568"),
                        transition: "all 0.15s",
                      }}
                    >
                      <Icon size={11} /> {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Filter chips */}
            {!naturalLanguageMode && (searchType === "organizations" || searchType === "all") && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", color: "#888" }}>Subscription:</span>
                {SUBSCRIPTION_FILTERS.map((f) => (
                  <button key={f.value || "any"} type="button" onClick={() => setSubscriptionFilter(f.value)}
                    style={{ padding: "4px 10px", fontSize: "12px", border: `1px solid ${subscriptionFilter === f.value ? "#7F7FD5" : (isDark ? "#2d3148" : "#e2e8f0")}`, borderRadius: "16px", background: subscriptionFilter === f.value ? "rgba(127,127,213,0.12)" : "transparent", color: subscriptionFilter === f.value ? "#7F7FD5" : (isDark ? "#a0aec0" : "#4a5568"), cursor: "pointer" }}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            {!naturalLanguageMode && (searchType === "loans" || searchType === "all") && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", color: "#888" }}>Stage:</span>
                {STAGE_FILTERS.map((f) => (
                  <button key={f.value || "any"} type="button" onClick={() => setStageFilter(f.value)}
                    style={{ padding: "4px 10px", fontSize: "12px", border: `1px solid ${stageFilter === f.value ? "#7F7FD5" : (isDark ? "#2d3148" : "#e2e8f0")}`, borderRadius: "16px", background: stageFilter === f.value ? "rgba(127,127,213,0.12)" : "transparent", color: stageFilter === f.value ? "#7F7FD5" : (isDark ? "#a0aec0" : "#4a5568"), cursor: "pointer" }}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {/* Natural language tips */}
            {naturalLanguageMode && (
              <div style={{ marginTop: "14px", padding: "12px 16px", background: isDark ? "#1a1f32" : "#f0f0ff", borderRadius: "8px", fontSize: "12px", color: isDark ? "#a0aec0" : "#4a5568" }}>
                <strong style={{ color: "#7F7FD5" }}>Tips:</strong>{" "}
                "Find user john@example.com" · "Loans for borrower Smith in Closing stage" · "Organizations on trial" · "Documents uploaded by jane" · "Loan number CA-12345"
              </div>
            )}
          </div>

          {/* Spinner */}
          {isLoading && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#7F7FD5" }}>
              <FaSpinner style={{ fontSize: "32px", animation: "spin 1s linear infinite" }} />
              <p style={{ marginTop: "12px", color: "#888" }}>Searching Firebase…</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && results === null && (
            <div style={{ textAlign: "center", padding: "64px 0", color: "#888" }}>
              <FaSearch style={{ fontSize: "48px", opacity: 0.2, marginBottom: "16px" }} />
              <p style={{ fontSize: "16px", fontWeight: 600 }}>Enter a search term and press Enter</p>
              <p style={{ fontSize: "13px", opacity: 0.7, maxWidth: "480px", margin: "8px auto 0" }}>
                Searches across all fields — name, email, phone, address, loan number, borrower, LO, document type, and more.
                Short terms like "jo" will match "John", "Johnson", "joao", etc.
              </p>
            </div>
          )}

          {/* Results */}
          {!isLoading && results !== null && (
            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: "20px", padding: "10px 16px",
                background: isDark ? "#161b2e" : "#fff",
                border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
                borderRadius: "8px",
              }}>
                <span style={{ fontSize: "14px", color: isDark ? "#a0aec0" : "#4a5568" }}>
                  Found <strong style={{ color: isDark ? "#e2e8f0" : "#1a202c" }}>{totalResults}</strong> result{totalResults !== 1 ? "s" : ""} for{" "}
                  <strong style={{ color: "#7F7FD5" }}>"{activeSearchTerm || "filters"}"</strong>
                  {" "}in <strong>{environment === "prod" ? "Production" : "Development"}</strong>
                </span>
                <button onClick={handleClear} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: "13px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <FaTimes size={11} /> Clear
                </button>
              </div>

              {totalResults === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0", color: "#888" }}>
                  <p style={{ fontSize: "16px" }}>No results found</p>
                  <p style={{ fontSize: "13px", opacity: 0.7 }}>Try a shorter term, different spelling, or switch to "All" type</p>
                </div>
              ) : (
                <div>
                  <ResultsSection title="Users" icon={FaUser} color="#7F7FD5" results={results.users} term={activeSearchTerm} isDark={isDark} CardComponent={UserResultCard} onViewDetails={navigateToDetail} />
                  <ResultsSection title="Organizations" icon={FaBuilding} color="#86efac" results={results.organizations} term={activeSearchTerm} isDark={isDark} CardComponent={OrgResultCard} onViewDetails={navigateToDetail} />
                  <ResultsSection title="Loans" icon={FaFileAlt} color="#fbbf24" results={results.loans} term={activeSearchTerm} isDark={isDark} CardComponent={LoanResultCard} onViewDetails={navigateToDetail} />
                  <ResultsSection title="Documents" icon={FaFileAlt} color="#a78bfa" results={results.documents} term={activeSearchTerm} isDark={isDark} CardComponent={DocumentResultCard} onViewDetails={navigateToDetail} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .GlobalSearchPageContainer [style*="cursor: pointer"]:hover { opacity: 0.85; }
      `}</style>
    </div>
  );
};

export default GlobalSearchPage;
